import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, EyeOff, CalendarDays, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { DocumentsSection } from "@/components/documents/DocumentsSection";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";

function money(minor: number | null | undefined, currency = "AZN", lang = "az") {
    if (minor == null) return "—";
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(minor / 100);
}

export default function EmployeeDetail() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const { hasPerm } = useAuth();
    const [tab, setTab] = useState<"overview" | "schedule" | "leaves" | "payroll">("overview");
    const [data, setData] = useState<any>(null);
    const [leaves, setLeaves] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [showSched, setShowSched] = useState(false);
    const [showRule, setShowRule] = useState(false);
    const canEditHr = hasPerm("hr.edit");
    const canEditSalary = hasPerm("hr.salary.view") || hasPerm("payroll.settings");

    const load = async () => {
        setLoading(true);
        try {
            const [emp, lv] = await Promise.all([
                api.get(`/hr/employees/${id}`),
                api.get(`/hr/leaves?employee_id=${id}`),
            ]);
            setData(emp.data); setLeaves(lv.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

    const removeSchedule = async (sid: string) => {
        if (!window.confirm(t("hr.schedule.confirm_remove"))) return;
        try { await api.delete(`/hr/employees/${id}/schedules/${sid}`); toast.success(t("hr.schedule.removed")); load(); }
        catch (e: any) { toast.error(formatApiError(e)); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>;
    if (err) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>;
    if (!data) return null;

    const dayLabel = (w: number) => String(t([`weekday.${w}`, `weekday.${w % 7}`]));
    const tabs = [
        { key: "overview", label: t("hr.tab.overview") },
        { key: "schedule", label: t("hr.tab.schedule") },
        { key: "leaves", label: t("hr.tab.leaves") },
        { key: "payroll", label: t("hr.tab.payroll") },
    ] as const;

    return (
        <div className="space-y-4" data-testid="hr-employee-detail">
            <div className="flex items-center gap-3">
                <Link to="/hr" className="inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: "var(--text-muted)" }}><ArrowLeft size={16} />{t("common.back")}</Link>
                <h1 className="text-xl font-semibold" style={{ color: "var(--text)" }} data-testid="hr-employee-name">
                    {data.user?.full_name || data.user?.email || data.position}
                </h1>
                <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--selected-bg)", color: "var(--accent)" }}>{String(t([`hr.status.${data.status}`, data.status]))}</span>
                {data.phone && <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="hr-employee-phone">{data.phone}<WhatsAppButton name={data.user?.full_name || data.user?.email || data.position} phone={data.phone} type="employee" id={data.id} testid="hr-employee-wa-btn" /></span>}
            </div>

            <div className="flex gap-1 border-b" style={{ borderColor: "var(--border)" }} data-testid="hr-tabs">
                {tabs.map((tt) => (
                    <button key={tt.key} data-testid={`hr-tab-${tt.key}`}
                        onClick={() => setTab(tt.key as any)}
                        className="px-3 py-2 text-sm border-b-2 transition-colors"
                        style={{ borderColor: tab === tt.key ? "var(--primary)" : "transparent", color: tab === tt.key ? "var(--text)" : "var(--text-muted)" }}>
                        {tt.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && id && <DocumentsSection entityType="employee" entityId={id} />}
            {tab === "overview" && (
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-xl border p-4 space-y-2 text-sm" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <div><span style={{ color: "var(--text-muted)" }}>{t("hr.employee.position")}: </span><span style={{ color: "var(--text)" }}>{data.position}</span></div>
                        <div><span style={{ color: "var(--text-muted)" }}>{t("hr.employee.contract")}: </span><span style={{ color: "var(--text)" }}>{String(t([`hr.contract.${data.contract_type}`, data.contract_type]))}</span></div>
                        <div><span style={{ color: "var(--text-muted)" }}>{t("hr.employee.hire_date")}: </span><span style={{ color: "var(--text)" }}>{data.hire_date || "—"}</span></div>
                        <div><span style={{ color: "var(--text-muted)" }}>{t("hr.employee.email")}: </span><span style={{ color: "var(--text)" }}>{data.user?.email || "—"}</span></div>
                    </div>
                </div>
            )}

            {tab === "schedule" && (
                <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    {canEditHr && (
                        <div className="flex justify-end">
                            <button data-testid="hr-sched-add" onClick={() => setShowSched(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                <Plus size={12} /> {t("hr.schedule.add")}
                            </button>
                        </div>
                    )}
                    {data.schedules?.length ? (
                        <ul className="space-y-1 text-sm">
                            {data.schedules.map((s: any) => (
                                <li key={s.id} data-testid={`hr-sched-${s.id}`} className="flex items-center gap-2">
                                    <CalendarDays size={14} style={{ color: "var(--text-muted)" }} />
                                    <span style={{ color: "var(--text)" }}>{dayLabel(s.weekday)}</span>
                                    <span style={{ color: "var(--text-muted)" }}>{s.start_time} – {s.end_time}</span>
                                    {canEditHr && (
                                        <button data-testid={`hr-sched-del-${s.id}`} onClick={() => removeSchedule(s.id)} className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border" style={{ borderColor: "var(--error-fg)", color: "var(--error-fg)" }}>
                                            <Trash2 size={10} /> {t("common.delete")}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("hr.schedule.empty")}</div>}
                    {showSched && <AddScheduleDialog employeeId={id!} onClose={() => setShowSched(false)} onSaved={() => { setShowSched(false); load(); }} />}
                </div>
            )}

            {tab === "leaves" && (
                <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="hr-leaves-list">
                    {leaves.length ? (
                        <table className="w-full text-sm">
                            <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                <th className="text-left px-2 py-1.5">{t("hr.leave.type")}</th>
                                <th className="text-left px-2 py-1.5">{t("hr.leave.range")}</th>
                                <th className="text-left px-2 py-1.5">{t("hr.leave.status")}</th>
                            </tr></thead>
                            <tbody>
                                {leaves.map((l: any) => (
                                    <tr key={l.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                                        <td className="px-2 py-1.5">{String(t([`hr.leave.type.${l.leave_type}`, l.leave_type]))}</td>
                                        <td className="px-2 py-1.5">{l.date_from} → {l.date_to}</td>
                                        <td className="px-2 py-1.5">{String(t([`hr.leave.status.${l.status}`, l.status]))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("hr.leaves.empty")}</div>}
                </div>
            )}

            {tab === "payroll" && (
                <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: "var(--border)", background: "var(--card)" }} data-testid="hr-payroll-rules">
                    {canEditSalary && (
                        <div className="flex justify-end">
                            <button data-testid="hr-rule-add" onClick={() => setShowRule(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                <Plus size={12} /> {t("hr.rules.add")}
                            </button>
                        </div>
                    )}
                    {(data.payroll_rules || []).length ? (
                        <ul className="space-y-2 text-sm">
                            {data.payroll_rules.map((r: any) => (
                                <li key={r.id} className="flex items-center gap-3 border-b pb-2" style={{ borderColor: "var(--border)" }} data-testid={`hr-rule-${r.id}`}>
                                    {r.program_id && (
                                        <span className="rounded-md px-2 py-0.5 text-xs border" style={{ borderColor: "var(--primary)", color: "var(--text)" }} data-testid={`hr-rule-program-${r.id}`}>{r.program_name || t("hr.rules.program")}</span>
                                    )}
                                    <span className="rounded-md px-2 py-0.5 text-xs" style={{ background: "var(--selected-bg)", color: "var(--accent)" }}>{String(t([`payroll.rule.${r.rule_type}`, r.rule_type]))}</span>
                                    <span style={{ color: "var(--text)" }}>
                                        {r.salary_masked ? (
                                            <span className="inline-flex items-center gap-1 tracking-wider" style={{ color: "var(--text-muted)" }} data-testid={`hr-rule-masked-${r.id}`}>
                                                <EyeOff size={13} /> ••••
                                            </span>
                                        ) : (r.amount_minor != null ? money(r.amount_minor, r.currency || "AZN", i18n.language) : (r.percentage != null ? `${r.percentage}%` : "—"))}
                                    </span>
                                    <span className="ml-auto text-xs" style={{ color: "var(--text-muted)" }}>{r.effective_from} → {r.effective_until || t("common.now")}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("hr.rules.empty")}</div>}
                    {showRule && <AddRuleDialog employeeId={id!} onClose={() => setShowRule(false)} onSaved={() => { setShowRule(false); load(); }} />}
                </div>
            )}
        </div>
    );
}

function AddScheduleDialog({ employeeId, onClose, onSaved }: { employeeId: string; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [state, setState] = useState({ weekday: 0, start_time: "09:00", end_time: "17:00" });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            await api.post(`/hr/employees/${employeeId}/schedules`, state);
            toast.success(t("hr.schedule.added"));
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="hr-sched-dialog">
            <div className="surface-card w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text)" }}>{t("hr.schedule.add")}</h3>
                <div className="space-y-3">
                    <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("hr.schedule.weekday")}</div>
                        <select data-testid="hr-sched-weekday" value={state.weekday} onChange={(e) => setState({ ...state, weekday: parseInt(e.target.value) })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                            {[0, 1, 2, 3, 4, 5, 6].map((w) => <option key={w} value={w}>{t(`weekday.${w}` as any)}</option>)}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.start_time")}</div><input data-testid="hr-sched-start" type="time" value={state.start_time} onChange={(e) => setState({ ...state, start_time: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.end_time")}</div><input data-testid="hr-sched-end" type="time" value={state.end_time} onChange={(e) => setState({ ...state, end_time: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                    </div>
                    {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                    <div className="flex gap-2">
                        <button data-testid="hr-sched-save" disabled={saving} onClick={submit} className="flex-1 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>{saving ? <Loader2 className="animate-spin inline" size={14} /> : <Save size={14} className="inline mr-1" />}{t("common.save")}</button>
                        <button onClick={onClose} className="rounded-md px-4 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 40 }}>{t("common.cancel")}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AddRuleDialog({ employeeId, onClose, onSaved }: { employeeId: string; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [state, setState] = useState({ rule_type: "fixed_monthly" as "fixed_monthly" | "hourly" | "per_lesson", amount: "", effective_from: new Date().toISOString().slice(0, 10), program_id: "" });
    const [progs, setProgs] = useState<{ id: string; name: string }[]>([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    useEffect(() => {
        api.get("/programs").then((r) => setProgs((r.data.items || []).map((x: any) => ({ id: x.id, name: x.name })))).catch(() => { /* optional */ });
    }, []);
    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            const minor = Math.round(parseFloat(state.amount) * 100);
            if (isNaN(minor) || minor < 0) { setErr(t("staff.invite_err.invalid_salary_amount")); return; }
            await api.post(`/hr/payroll-rules`, {
                employee_id: employeeId, rule_type: state.rule_type,
                amount_minor: minor, effective_from: state.effective_from,
                program_id: state.program_id || null,
            });
            toast.success(t("hr.rules.added"));
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="hr-rule-dialog">
            <div className="surface-card w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text)" }}>{t("hr.rules.add")}</h3>
                <div className="space-y-3">
                    <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_model")}</div>
                        <select data-testid="hr-rule-type" value={state.rule_type} onChange={(e) => setState({ ...state, rule_type: e.target.value as any })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                            <option value="fixed_monthly">{t("staff.invite.salary_fixed")}</option>
                            <option value="hourly">{t("staff.invite.salary_hourly")}</option>
                            <option value="per_lesson">{t("staff.invite.salary_per_lesson")}</option>
                        </select>
                    </label>
                    {progs.length > 0 && (
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("hr.rules.program")}</div>
                            <select data-testid="hr-rule-program" value={state.program_id} onChange={(e) => setState({ ...state, program_id: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                                <option value="">{t("hr.rules.program_none")}</option>
                                {progs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_amount")}</div><input data-testid="hr-rule-amount" inputMode="decimal" value={state.amount} onChange={(e) => setState({ ...state, amount: e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                    <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("hr.rules.effective_from")}</div><input data-testid="hr-rule-from" type="date" value={state.effective_from} onChange={(e) => setState({ ...state, effective_from: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                    {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                    <div className="flex gap-2">
                        <button data-testid="hr-rule-save" disabled={saving || !state.amount} onClick={submit} className="flex-1 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>{saving ? <Loader2 className="animate-spin inline" size={14} /> : <Save size={14} className="inline mr-1" />}{t("common.save")}</button>
                        <button onClick={onClose} className="rounded-md px-4 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 40 }}>{t("common.cancel")}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
